import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { HeroVisual } from './FuturisticVisuals';

export default function Hero() {
  return (
    <section
      id="home"
      className="relative overflow-hidden min-h-[88vh] flex items-center pt-32 pb-20 sm:pt-36 sm:pb-24"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1b35] via-[#0d2143] to-[#15315c]" />
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full animate-drift" style={{ background: 'radial-gradient(circle, rgba(232,184,99,0.16) 0%, transparent 65%)' }} />
      <div className="absolute -bottom-40 -left-40 w-[620px] h-[620px] rounded-full animate-drift" style={{ background: 'radial-gradient(circle, rgba(37,80,143,0.35) 0%, transparent 65%)', animationDelay: '3s' }} />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7 fade-in-up text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e8b863]/35 bg-[#e8b863]/8 backdrop-blur-sm mb-7">
            <Sparkles className="w-3.5 h-3.5 text-[#e8b863]" />
            <span className="text-[#e8b863] text-[11px] tracking-[0.26em] font-semibold uppercase">
              Premium Consulting Firm
            </span>
          </div>

          <h1 className="font-serif text-white font-medium leading-[1.05] text-[40px] sm:text-[54px] lg:text-[70px] xl:text-[76px] tracking-tight">
            Clarity, capital,
            <br />
            and <span className="italic gold-gradient">capability</span>
            <br />
            <span className="text-white/95">— delivered.</span>
          </h1>

          <p className="mt-7 sm:mt-8 text-white/78 text-[15.5px] sm:text-[17px] lg:text-[18px] leading-relaxed max-w-xl mx-auto lg:mx-0">
            Salihi Consulting Services partners with governments, enterprises, and
            development institutions to design and execute the financial, operational,
            and technological transformations that move organizations forward.
          </p>

          <div className="mt-9 sm:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-[#d49d3f] to-[#b8842a] hover:from-[#e8b863] hover:to-[#d49d3f] text-[#0a1b35] font-semibold text-[14px] tracking-wide transition-all duration-300 shadow-xl shadow-[#d49d3f]/25 hover:shadow-2xl hover:-translate-y-0.5"
            >
              Book a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full border border-white/22 hover:border-[#e8b863]/55 text-white text-[14px] font-medium tracking-wide hover:bg-white/5 transition-all duration-300"
            >
              Explore Services
            </Link>
          </div>

          <div className="mt-12 sm:mt-14 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 sm:gap-x-8 gap-y-3 text-white/80 text-[11px] sm:text-[12px] tracking-[0.18em] uppercase">
            <span className="text-white/65">Trusted by</span>
            <span className="font-semibold">World Bank Group</span>
            <span className="text-[#e8b863]">•</span>
            <span className="font-semibold">African Development Bank</span>
            <span className="text-[#e8b863] hidden sm:inline">•</span>
            <span className="font-semibold">Ministry of Finance</span>
          </div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5 relative fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="relative max-w-[500px] mx-auto">
            <div className="absolute -inset-4 rounded-[2rem] border border-[#e8b863]/15" />
            <div className="absolute -inset-1 rounded-[1.6rem] bg-gradient-to-br from-[#e8b863]/22 via-transparent to-[#25508f]/35 blur-xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)] border border-white/12 aspect-square bg-[#0a1b35]">
              <HeroVisual className="w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1b35]/35 via-transparent to-transparent pointer-events-none" />
            </div>

            <div className="absolute top-5 -left-3 sm:-left-5 bg-[#0a1b35]/88 backdrop-blur-md border border-[#e8b863]/35 rounded-2xl px-4 py-3 shadow-2xl">
              <p className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold">Expert advisors</p>
              <p className="text-white text-[13px] font-semibold mt-0.5">Senior-led engagements</p>
            </div>
            <div className="absolute bottom-8 -right-3 sm:-right-5 bg-[#0a1b35]/88 backdrop-blur-md border border-[#e8b863]/35 rounded-2xl px-4 py-3 shadow-2xl">
              <p className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold">Multi-sector</p>
              <p className="text-white text-[13px] font-semibold mt-0.5">15 Service Lines</p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0d2143] to-transparent pointer-events-none" />
    </section>
  );
}
